import { Extension, Node, mergeAttributes } from "@tiptap/core";
import { inlineIconSvg } from "@bcc/core-renderers";

/**
 * Tiptap building blocks shared by the two rich-text editors:
 *  - `ParagraphRichEditor` (inline, one paragraph : TextBlock items, titles)
 *  - `RichTextEditorField` (full editorial body of the RichText block)
 *
 * The inline icon node round-trips the wire format the sanitisers preserve
 * (`<span data-icon="name"></span>`) while showing the real SVG inside the
 * editor via a node view.
 */

/** Inline atom node for `<span data-icon="name"></span>` markers. */
export const InlineIcon = Node.create({
  name: "inlineIcon",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      name: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-icon"),
        renderHTML: (attributes: { name?: string | null }) =>
          attributes.name ? { "data-icon": attributes.name } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-icon]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes(HTMLAttributes)];
  },

  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement("span");
      const name = String(node.attrs.name ?? "");
      dom.setAttribute("data-icon", name);
      dom.setAttribute("contenteditable", "false");
      dom.className =
        "inline-flex h-[1.15em] w-[1.15em] shrink-0 translate-y-[0.15em] items-center justify-center rounded-sm bg-slate-100 text-[#005CA9] ring-1 ring-slate-200";
      dom.innerHTML = inlineIconSvg(name) || "·";
      return { dom };
    };
  },
});

/** Custom top node hosting exactly ONE paragraph (inline editor). */
export const SingleParagraphDocument = Node.create({
  name: "doc",
  topNode: true,
  content: "paragraph",
});

/** In the single-paragraph editor, Enter inserts a line break instead of splitting. */
export const EnterInsertsBreak = Extension.create({
  name: "enterInsertsBreak",
  addKeyboardShortcuts() {
    return {
      Enter: () => this.editor.commands.setHardBreak(),
    };
  },
});

/** Client-side mirror of the sanitisers' scheme check (UX guard before save). */
export function isSafeEditorHref(href: string): boolean {
  const v = href.trim().toLowerCase();
  const stripped = Array.from(v)
    .filter((ch) => ch.charCodeAt(0) > 32)
    .join("");
  return stripped.length > 0 && !/^(?:javascript|vbscript|data):/i.test(stripped);
}

/**
 * Converts the single-paragraph editor's document HTML (`<p>…</p>`) to the
 * inline HTML string the content model stores (no block wrapper, `<br>` for
 * line breaks).
 */
export function tiptapDocToInlineHtml(html: string): string {
  const trimmed = (html ?? "").trim();
  const match = /^<p>([\s\S]*)<\/p>$/.exec(trimmed);
  const inner = match ? match[1] : trimmed;
  return inner === "<br>" ? "" : inner;
}
